// pages/AuthPage/AuthPage.tsx
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Fade,
  Grid,
  InputAdornment,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { validarCodigo } from '../../services/api';
import toast from 'react-hot-toast';

const AuthPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [codigo, setCodigo] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validationResult, setValidationResult] = useState<any>(null);

  const handleChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newCodigo = [...codigo];
      newCodigo[index] = value;
      setCodigo(newCodigo);
      setError('');
      
      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`code-input-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !codigo[index] && index > 0) {
      const prevInput = document.getElementById(`code-input-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const codigoCompleto = codigo.join('');
    if (codigoCompleto.length !== 6) {
      setError('Digite todos os 6 dígitos do código');
      return;
    }

    setError('');
    setLoading(true);

    try {
      toast.loading('Validando código...', { id: 'code-validation' });
      const response = await validarCodigo(token!, codigoCompleto);
      
      setValidationResult(response);
      setSuccess(true);
      
      // Salvar token de sessão
      if (response.session_token) {
        localStorage.setItem('token', response.session_token);
        localStorage.setItem('servidorId', response.servidor.id);
      }
      
      toast.success('Código validado com sucesso!', { id: 'code-validation' });
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Erro ao validar código';
      setError(errorMsg);
      toast.error(errorMsg, { id: 'code-validation' });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocuments = () => {
    if (validationResult?.servidor?.id) {
      navigate(`/documentos/${validationResult.servidor.id}`);
    }
  };

  return (
    <Fade in={true} timeout={500}>
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          {!success ? (
            <>
              <KeyIcon 
                sx={{ 
                  fontSize: 60, 
                  color: 'primary.main',
                  mb: 2 
                }} 
              />
              
              <Typography variant="h5" gutterBottom color="primary.main">
                Inserir Código de Validação
              </Typography>
              
              <Typography variant="body1" paragraph color="text.secondary">
                Digite o código de 6 dígitos que você recebeu por email.
              </Typography>

              <form onSubmit={handleSubmit}>
                <Grid container spacing={1} justifyContent="center" sx={{ mb: 3 }}>
                  {codigo.map((digit, index) => (
                    <Grid item key={index}>
                      <TextField
                        id={`code-input-${index}`}
                        value={digit}
                        onChange={(e) => handleChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        inputProps={{
                          maxLength: 1,
                          style: { 
                            textAlign: 'center',
                            fontSize: '1.5rem',
                            fontWeight: 'bold'
                          }
                        }}
                        sx={{
                          width: 50,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                        disabled={loading}
                        autoFocus={index === 0}
                      />
                    </Grid>
                  ))}
                </Grid>

                {error && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || codigo.join('').length !== 6}
                  sx={{ 
                    mt: 2, 
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 'bold'
                  }}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Validando...
                    </>
                  ) : (
                    'Validar Código'
                  )}
                </Button>
              </form>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
                <strong>Importante:</strong> O código expira em 15 minutos
              </Typography>
            </>
          ) : (
            <>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: 'success.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 50, color: 'success.main' }} />
              </Box>
              
              <Typography variant="h4" gutterBottom color="success.main">
                Validação Concluída!
              </Typography>
              
              <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                Sua identidade foi verificada com sucesso
              </Alert>

              {validationResult?.servidor && (
                <Box sx={{ 
                  p: 3, 
                  backgroundColor: 'primary.light', 
                  borderRadius: 2,
                  mb: 3 
                }}>
                  <Typography variant="h6" color="white" gutterBottom>
                    {validationResult.servidor.nome}
                  </Typography>
                  <Typography variant="body2" color="white">
                    CPF: {validationResult.servidor.cpf}
                  </Typography>
                </Box>
              )}

              <Typography variant="body1" paragraph>
                Agora você pode acessar seus documentos e proceder com a assinatura digital.
              </Typography>

              <Button
                variant="contained"
                size="large"
                onClick={handleViewDocuments}
                sx={{
                  px: 6,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  borderRadius: 2,
                }}
              >
                Acessar Meus Documentos
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </Fade>
  );
};

export default AuthPage;